'use client';

import Link from 'next/link';
import Image from 'next/image';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Check, Sparkles } from 'lucide-react';

const freeFeatures = [
  'Unlimited artwork storage',
  'Online art gallery',
  'Share with family',
  'AI story generation',
  'Auto-tagging & organization',
];

const bookOptions = [
  {
    name: 'Mini Book',
    size: '6×6"',
    pages: '12 pages',
    price: 29,
    description: 'Perfect for a single season or special occasion',
    image: '/marketing/book-mini.png?v=2',
  },
  {
    name: 'Standard Book',
    size: '8.5×8.5"',
    pages: '20 pages',
    price: 49,
    description: 'Our most popular choice for yearly collections',
    popular: true,
    image: '/marketing/book-standard.png?v=2',
  },
  {
    name: 'Premium Book',
    size: '10×10"',
    pages: '30 pages',
    price: 69,
    description: 'The ultimate keepsake with extra pages',
    image: '/marketing/book-premium.png?v=2',
  },
];

/**
 * Pricing section - Free app + paid books
 */
export function Pricing() {
  return (
    <section id="pricing" className="py-16 md:py-24 bg-cream-200">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-6xl">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif font-semibold text-charcoal-600 mb-4">
            Free to Start. Keepsakes When You&apos;re Ready.
          </h2>
          <p className="text-taupe-500 text-base md:text-lg max-w-2xl mx-auto">
            Use TinyChapters completely free. Order beautiful printed books whenever you want.
          </p>
        </motion.div>

        {/* Free App Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="bg-white rounded-2xl shadow-md p-6 md:p-8 mb-12"
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="bg-sage-100 text-sage-600 text-xs font-semibold px-3 py-1 rounded-full">
                  Always Free
                </span>
                <h3 className="text-xl md:text-2xl font-serif font-semibold text-charcoal-600">
                  TinyChapters App
                </h3>
              </div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {freeFeatures.map((feature) => (
                  <li key={feature} className="flex items-center gap-2 text-taupe-500">
                    <Check className="w-4 h-4 text-sage-500 flex-shrink-0" />
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex-shrink-0">
              <Link href="/signup">
                <Button className="bg-terracotta-500 hover:bg-terracotta-600 text-white px-8 py-3 text-base">
                  Get Started Free
                </Button>
              </Link>
            </div>
          </div>
        </motion.div>

        {/* Printed Books Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="text-center mb-8"
        >
          <h3 className="text-xl md:text-2xl font-serif font-semibold text-charcoal-600 mb-2">
            Printed Storybooks
          </h3>
          <p className="text-taupe-500 text-sm md:text-base">
            Turn your child&apos;s artwork and stories into beautiful printed books
          </p>
        </motion.div>

        {/* Book Cards */}
        <div className="grid md:grid-cols-3 gap-6">
          {bookOptions.map((book, index) => (
            <motion.div
              key={book.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
              className={`relative bg-white rounded-2xl shadow-md p-6 ${
                book.popular ? 'ring-2 ring-terracotta-500' : ''
              }`}
            >
              {book.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-terracotta-500 text-white text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1">
                    <Sparkles className="w-3 h-3" />
                    Most Popular
                  </span>
                </div>
              )}

              {/* Book Preview Image */}
              <div className="w-full aspect-square bg-cream-100 rounded-xl mb-4 overflow-hidden relative">
                <Image
                  src={book.image}
                  alt={book.name}
                  fill
                  className="object-contain p-4"
                />
              </div>

              <h4 className="text-lg font-semibold text-charcoal-600 mb-1">
                {book.name}
              </h4>
              <p className="text-sm text-taupe-400 mb-2">
                {book.size} · {book.pages}
              </p>
              <p className="text-xs text-taupe-500 mb-4">
                {book.description}
              </p>

              <div className="flex items-baseline gap-1 mb-4">
                <span className="text-2xl font-semibold text-charcoal-600">
                  ${book.price}
                </span>
                <span className="text-sm text-taupe-400">per book</span>
              </div>

              <Link href="/signup">
                <Button
                  variant={book.popular ? 'default' : 'outline'}
                  className={`w-full ${
                    book.popular
                      ? 'bg-terracotta-500 hover:bg-terracotta-600 text-white'
                      : 'border-taupe-300 text-charcoal-600 hover:bg-cream-100'
                  }`}
                >
                  Create Book
                </Button>
              </Link>
            </motion.div>
          ))}
        </div>

        {/* Note */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="text-center text-sm text-taupe-400 mt-8"
        >
          Prices include shipping within the US. International shipping available.
        </motion.p>
      </div>
    </section>
  );
}
